import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getTimezoneDisplay } from '../../helpers'
import { COMMON_TIMEZONES } from '../../constants'

interface ITimezoneSelectorProps {
  selectedTimezone: string
  onTimezoneChange: (timezone: string) => void
  deviceTimezone: string
}

export const TimezoneSelector: React.FC<ITimezoneSelectorProps> = ({
  selectedTimezone,
  onTimezoneChange,
  deviceTimezone,
}) => {
  const { t } = useTranslation()
  const [showDropdown, setShowDropdown] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Filter timezones based on search query
  const filteredTimezones = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return COMMON_TIMEZONES
    }

    const lowerQuery = searchQuery.toLowerCase()
    return COMMON_TIMEZONES.filter((timezone) => {
      const displayName = getTimezoneDisplay(timezone).toLowerCase()
      return displayName.includes(lowerQuery)
    })
  }, [searchQuery])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false)
        setSearchQuery('')
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      if (showDropdown) {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showDropdown])

  const handleTimezoneSelect = (timezone: string) => {
    onTimezoneChange(timezone)
    setShowDropdown(false)
    setSearchQuery('')
  }

  return (
    <div className="relative flex-1 min-w-0" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="text-sm text-primary hover:underline flex items-center gap-1"
      >
        {t('calendar.modal.timeZone')} {deviceTimezone}
      </button>

      {/* Timezone Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-primary-outline rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto w-96">
          <div className="p-2">
            <input
              type="text"
              placeholder="Search timezone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-primary-outline rounded-md focus:outline-none focus:border-primary mb-2"
              autoFocus
            />
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((timezone) => (
                <button
                  key={timezone}
                  onClick={() => handleTimezoneSelect(timezone)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-brandLight rounded transition-colors ${
                    selectedTimezone === timezone
                      ? 'bg-primary-brandLight text-primary font-medium'
                      : 'text-primary-text'
                  }`}
                >
                  {getTimezoneDisplay(timezone)}
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-silver-light text-center">
                No timezones found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
